import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { REFRESH_INTERVAL } from 'configs/constants';
import { useLocationAssetsWithAlertsDataSource } from 'custom_hooks/data_hooks/data_source/locationAssetsWithAlertsDataSource';
import { AlertItem } from 'pages/Location/alerts_and_drafts/assets_with_alerts/alert_item/AlertItem';
import {
    AssetsWithAlertsContent,
    EmptyState,
    Header,
    HeaderRightContainer,
    ScrollContainer,
    Title,
} from 'pages/Location/alerts_and_drafts/assets_with_alerts/styled';
import { WorkbenchLink } from 'pages/Location/alerts_and_drafts/WorkbenchLink';
import { VerticalDivider } from 'pages/Location/issues/styled';
import { forwardRef, memo, useImperativeHandle, useMemo, useState } from 'react';
import { OverviewStatusLozenge } from 'shared/lozenge/OverviewStatusLozenge';

export type LocationAssetsWithAlertsWidgetRef = {
    refetch: () => Promise<unknown>;
};

type LocationAssetsWithAlertsWidgetProps = Readonly<{
    locationId: string;
    hideHeader?: boolean;
}>;

export type ExpandedState = Record<string, boolean>;

const PAGE_LIMIT = 10;

function LocationAssetsWithAlertsWidget(
    { locationId, hideHeader = false }: LocationAssetsWithAlertsWidgetProps,
    ref: React.ForwardedRef<LocationAssetsWithAlertsWidgetRef>
) {
    const [expandedState, setExpandedState] = useState<ExpandedState>({});

    const {
        results: data,
        isLoading,
        refetch,
        hasNext,
        loadNext,
    } = useLocationAssetsWithAlertsDataSource({
        name: 'location-assets-with-alerts',
        defaultFilter: { locationId },
        pageLimit: PAGE_LIMIT,
        keepPreviousData: true,
        enabled: true,
        refetchInterval: REFRESH_INTERVAL,
        staleTime: REFRESH_INTERVAL,
    });

    useImperativeHandle(
        ref,
        () => ({
            refetch,
        }),
        [refetch]
    );

    const toggleExpanded = (alertId: string) => {
        setExpandedState((prev) => ({
            ...prev,
            [alertId]: !prev[alertId],
        }));
    };

    const alertList = useMemo(
        () =>
            data?.map((alert) => (
                <AlertItem
                    key={alert.Id}
                    alert={alert}
                    expanded={!!expandedState[alert.Id]}
                    onToggle={() => toggleExpanded(alert.Id)}
                    locationId={locationId}
                />
            )) ?? [],
        [data, expandedState, locationId]
    );

    if (!isLoading && !data?.length) {
        return (
            <>
                {!hideHeader && (
                    <Header>
                        <Title>Assets with Alerts</Title>
                        <HeaderRightContainer>
                            <OverviewStatusLozenge>PAST 30 DAYS</OverviewStatusLozenge>
                            <VerticalDivider />
                            <WorkbenchLink locationId={locationId} />
                        </HeaderRightContainer>
                    </Header>
                )}
                <EmptyState>No assets with alerts found.</EmptyState>
            </>
        );
    }

    return (
        <SpinnerArea isLoading={isLoading}>
            {!hideHeader && (
                <Header>
                    <Title>Assets with Alerts</Title>
                    <HeaderRightContainer>
                        <OverviewStatusLozenge>PAST 30 DAYS</OverviewStatusLozenge>
                        <VerticalDivider />
                        <WorkbenchLink locationId={locationId} />
                    </HeaderRightContainer>
                </Header>
            )}
            <AssetsWithAlertsContent>
                <ScrollContainer
                    onLoadMore={loadNext}
                    enabled={!!hasNext}
                    isLoading={isLoading && !data}
                    aria-label="Assets with alerts list"
                    infiniteScrollDirection="bottom"
                >
                    {alertList}
                </ScrollContainer>
            </AssetsWithAlertsContent>
        </SpinnerArea>
    );
}

const memoLocationAssetsWithAlertsWidget = memo(
    forwardRef<LocationAssetsWithAlertsWidgetRef, LocationAssetsWithAlertsWidgetProps>(
        LocationAssetsWithAlertsWidget
    )
);
export { memoLocationAssetsWithAlertsWidget as LocationAssetsWithAlertsWidget };
